import { useTranslation } from 'react-i18next';
import { FilterChip } from './QueueParts';
import { CLOSED_RANGE_PRESETS, type ClosedRange } from './queueView';

const DATE_INPUT =
  'min-h-[48px] w-full rounded-lg border border-slate-700 bg-slate-900 px-2 text-sm text-slate-100';

/**
 * The bound on the Closed archive (D3). Seven days by default, wider on request, and a
 * custom pair of dates. There is no pagination: the range *is* the bound, and a range
 * that fills the page is reported as such rather than paged.
 *
 * Shown on the Closed tab only. New, In Progress and To Verify have no date control
 * because they have no cutoff — an open job nobody has touched in eight days is the
 * single most important row on the screen.
 */
export function ClosedRangeControl({
  range,
  onChange,
}: {
  range: ClosedRange;
  onChange: (next: ClosedRange) => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="border-b border-slate-800 px-3 py-2">
      <div
        role="group"
        aria-label={t('queue.range.label')}
        className="flex gap-2 overflow-x-auto"
      >
        {CLOSED_RANGE_PRESETS.map((preset) => (
          <FilterChip
            key={preset}
            label={t(`queue.range.${preset}`)}
            active={range.preset === preset}
            onClick={() => onChange({ ...range, preset })}
          />
        ))}
      </div>
      {range.preset === 'custom' && (
        <div className="mt-2 flex gap-2">
          <label className="flex flex-1 flex-col gap-1 text-xs text-slate-400">
            {t('queue.range.from')}
            <input
              type="date"
              value={range.from}
              max={range.to}
              // A cleared input hands back an empty string. Keeping the last real date
              // means the archive is always bounded, which is the whole point of it.
              onChange={(event) =>
                event.target.value && onChange({ ...range, from: event.target.value })
              }
              className={DATE_INPUT}
            />
          </label>
          <label className="flex flex-1 flex-col gap-1 text-xs text-slate-400">
            {t('queue.range.to')}
            <input
              type="date"
              value={range.to}
              min={range.from}
              onChange={(event) =>
                event.target.value && onChange({ ...range, to: event.target.value })
              }
              className={DATE_INPUT}
            />
          </label>
        </div>
      )}
    </div>
  );
}
